import React, { useCallback } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { ALREADY_DELEGATED_MESSAGE, mapToErrorMessage } from '@components/Web3/utils'
import { SECOND_MS } from 'data/constants'
import { GetSigner } from 'privy/WalletReady'
import { SignerUndefinedError, useRedelegateStakeTransaction } from 'use-towns-client'

export type SubmitRedelegateArgs = {
    depositIds: bigint[]
    delegatee: `0x${string}`
    operatorAddress?: `0x${string}`
    getSigner: GetSigner
    onAlreadyDelegated: () => void
}

export const useRedelegateStake = () => {
    const { redelegateToTown, ...transactionRest } = useRedelegateStakeTransaction()

    return {
        ...transactionRest,
        submitRedelegate: useCallback(
            async (args: SubmitRedelegateArgs) => {
                console.log('[redelegate-to-town] submitRedelegate', args)
                const { depositIds, delegatee, operatorAddress, getSigner, onAlreadyDelegated } =
                    args

                const signer = await getSigner()
                if (!signer) {
                    throw new SignerUndefinedError()
                }

                if (!redelegateToTown) {
                    return toasts.error(
                        new Error('Redelegation is not available.'),
                        onAlreadyDelegated,
                    )
                }

                toasts.submitting()

                try {
                    return new Promise<void>((resolve, reject) => {
                        redelegateToTown(
                            {
                                signer,
                                depositIds,
                                delegatee,
                                operatorAddress,
                            },
                            {
                                onSuccess: () => {
                                    toasts.success()
                                    resolve()
                                },
                                onError: (error: Error) => {
                                    toasts.error(error, onAlreadyDelegated)
                                    reject(error)
                                },
                            },
                        )
                    })
                } catch (error) {
                    console.error('Error during redelegation process:', error)
                    const catchedError =
                        error instanceof Error
                            ? error
                            : new Error('An unexpected error occurred during redelegation.')
                    toasts.error(catchedError, onAlreadyDelegated)
                }
            },
            [redelegateToTown],
        ),
    }
}

const toasts = {
    submitting: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Pending toast={toast} message="Submitting redelegation..." />
            ),
            {
                id: 'redelegate-tx',
                duration: Number.POSITIVE_INFINITY,
            },
        )
    },

    success: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message="Successfully redelegated!"
                    subMessage="Your stake has been redelegated to the new town."
                />
            ),
            { id: 'redelegate-tx', duration: SECOND_MS * 4 },
        )
    },

    error: (error: Error, onAlreadyDelegated: () => void) => {
        console.error(error.message)
        const mappedError = mapToErrorMessage({ error, source: 'redelegate-stake' })
        if (mappedError?.includes(ALREADY_DELEGATED_MESSAGE)) {
            popupToast(
                ({ toast }) => (
                    <StandardToast.Success
                        toast={toast}
                        message="This town was just delegated. Please retry to complete your redelegation."
                    />
                ),
                { id: 'redelegate-tx', duration: SECOND_MS * 25 },
            )
            onAlreadyDelegated?.()
            return
        }

        popupToast(
            ({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Redelegation failed"
                    subMessage={error.message}
                />
            ),
            { id: 'redelegate-tx', duration: SECOND_MS * 5 },
        )
    },
}
